import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import Pill from '../ui/Pill';
import StatusBanner from '../ui/StatusBanner';
import './PlatformBillingTab.css';

/**
 * Billing MVP — Option B Platform Admin tab.
 *
 * Internal subscription management. No Stripe code; no Checkout;
 * no Customer Portal. Operators set state manually.
 *
 * Renders inside the existing CompanyManagementModal. Receives
 * the companyId from the parent and stays self-contained.
 */

const STATUS_LABELS = {
  trial: 'Trial',
  active: 'Active',
  suspended: 'Suspended',
  cancelled: 'Cancelled'
};

const STATUS_TONES = {
  trial: 'info',
  active: 'success',
  suspended: 'warning',
  cancelled: 'neutral'
};

const VALID_TRANSITIONS = {
  trial: ['active', 'suspended', 'cancelled'],
  active: ['suspended', 'cancelled'],
  suspended: ['active', 'cancelled'],
  cancelled: ['trial', 'active']
};

function PlatformBillingTab({ companyId, companyName }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    planKey: '',
    seatLimit: '',
    trialEndsAt: '',
    notes: ''
  });
  const [statusBusy, setStatusBusy] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError('');
    try {
      const payload = await apiRequest(`/api/platform/companies/${companyId}/subscription`);
      setData(payload);
      const sub = payload?.subscription;
      setForm({
        planKey: sub?.planKey || '',
        seatLimit: sub?.seatLimit == null ? '' : String(sub.seatLimit),
        trialEndsAt: sub?.trialEndsAt ? String(sub.trialEndsAt).slice(0, 10) : '',
        notes: sub?.notes || ''
      });
    } catch (err) {
      setError(err?.message || 'Unable to load subscription.');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const subscription = data?.subscription || null;
  const counters = data?.counters || { employeeCount: 0, activeUserCount: 0 };
  const seatLimit = subscription?.seatLimit;
  const seatPercent = useMemo(() => {
    if (!seatLimit || seatLimit <= 0) return null;
    return Math.min(100, Math.round((counters.activeUserCount / seatLimit) * 100));
  }, [seatLimit, counters.activeUserCount]);
  const seatOver = seatLimit && counters.activeUserCount > seatLimit;

  const handleStatusChange = useCallback(async (nextStatus) => {
    setStatusBusy(nextStatus);
    setError('');
    try {
      await apiRequest(`/api/platform/companies/${companyId}/subscription/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus })
      });
      await fetchSubscription();
    } catch (err) {
      setError(err?.message || `Unable to transition to ${nextStatus}.`);
    } finally {
      setStatusBusy('');
    }
  }, [companyId, fetchSubscription]);

  const handleSave = useCallback(async (event) => {
    event?.preventDefault?.();
    setSaveBusy(true);
    setError('');
    try {
      const body = {
        status: subscription?.status || 'trial',
        planKey: form.planKey || null,
        seatLimit: form.seatLimit === '' ? null : Number(form.seatLimit),
        trialEndsAt: form.trialEndsAt ? new Date(form.trialEndsAt).toISOString() : null,
        notes: form.notes || null
      };
      await apiRequest(`/api/platform/companies/${companyId}/subscription`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      await fetchSubscription();
    } catch (err) {
      setError(err?.message || 'Unable to save subscription.');
    } finally {
      setSaveBusy(false);
    }
  }, [companyId, form, subscription, fetchSubscription]);

  const currentStatus = subscription?.status || null;
  const allowedTransitions = currentStatus ? (VALID_TRANSITIONS[currentStatus] || []) : ['trial', 'active'];

  return (
    <section
      className="director-card platform-billing-tab"
      data-testid="platform-billing-tab"
    >
      <div className="role-section-header">
        <h2>Billing</h2>
        {subscription ? (
          <Pill variant={STATUS_TONES[subscription.status] || 'neutral'}>
            {STATUS_LABELS[subscription.status] || subscription.status}
          </Pill>
        ) : (
          <Pill variant="neutral">No subscription</Pill>
        )}
      </div>

      {error ? <StatusBanner kind="error" message={error} live /> : null}
      {isLoading ? (
        <div className="platform-billing-loading">Loading subscription…</div>
      ) : (
        <>
          <dl className="platform-billing-counters">
            <div className="platform-billing-counter-card" data-testid="platform-billing-employee-count">
              <dt>Employee profiles</dt>
              <dd>{counters.employeeCount}</dd>
            </div>
            <div className="platform-billing-counter-card" data-testid="platform-billing-active-user-count">
              <dt>Active users</dt>
              <dd>
                {counters.activeUserCount}
                {seatLimit ? ` / ${seatLimit}` : null}
              </dd>
              {seatPercent != null ? (
                <div className={`platform-billing-seat-bar${seatOver ? ' platform-billing-seat-bar--over' : ''}`}>
                  <div
                    className="platform-billing-seat-bar-fill"
                    style={{ width: `${Math.min(100, seatPercent)}%` }}
                    data-testid="platform-billing-seat-bar-fill"
                  />
                </div>
              ) : null}
            </div>
            <div className="platform-billing-counter-card" data-testid="platform-billing-trial-ends">
              <dt>Trial ends</dt>
              <dd>{subscription?.trialEndsAt ? new Date(subscription.trialEndsAt).toLocaleDateString() : '—'}</dd>
            </div>
            <div className="platform-billing-counter-card" data-testid="platform-billing-plan-key">
              <dt>Plan</dt>
              <dd>{subscription?.planKey || '—'}</dd>
            </div>
          </dl>

          <form className="platform-billing-form" onSubmit={handleSave}>
            <label>
              Plan key
              <input
                value={form.planKey}
                onChange={(e) => setForm({ ...form, planKey: e.target.value })}
                placeholder="starter / growth / enterprise"
                data-testid="platform-billing-form-plan-key"
              />
            </label>
            <label>
              Seat limit
              <input
                type="number"
                min="0"
                value={form.seatLimit}
                onChange={(e) => setForm({ ...form, seatLimit: e.target.value })}
                placeholder="(unlimited)"
                data-testid="platform-billing-form-seat-limit"
              />
            </label>
            <label>
              Trial ends at
              <input
                type="date"
                value={form.trialEndsAt}
                onChange={(e) => setForm({ ...form, trialEndsAt: e.target.value })}
                data-testid="platform-billing-form-trial-ends"
              />
            </label>
            <label className="platform-billing-form-notes">
              Notes
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="platform-billing-form-notes"
              />
            </label>
            <div className="director-form-actions platform-billing-form-actions">
              <Button
                size="sm"
                type="submit"
                disabled={saveBusy}
                data-testid="platform-billing-save"
              >
                {saveBusy ? 'Saving…' : (subscription ? 'Save' : 'Create subscription')}
              </Button>
            </div>
          </form>

          <div className="platform-billing-status-actions" role="group" aria-label="Subscription status actions">
            <div className="platform-billing-status-actions-label">Set status</div>
            {['trial', 'active', 'suspended', 'cancelled'].map((status) => {
              const isCurrent = currentStatus === status;
              const isAllowed = isCurrent || allowedTransitions.includes(status);
              return (
                <Button
                  key={status}
                  size="sm"
                  variant={isCurrent ? 'primary' : 'secondary'}
                  disabled={!isAllowed || statusBusy === status}
                  onClick={() => handleStatusChange(status)}
                  data-testid={`platform-billing-set-${status}`}
                >
                  {statusBusy === status ? '…' : STATUS_LABELS[status]}
                </Button>
              );
            })}
          </div>

          {subscription?.statusChangedAt ? (
            <p className="platform-billing-status-meta">
              Status set {new Date(subscription.statusChangedAt).toLocaleString()}
              {subscription.statusChangedByUserId ? ` by user #${subscription.statusChangedByUserId}` : null}
            </p>
          ) : null}

          {companyName ? (
            <p className="platform-billing-tenant-label">{companyName}</p>
          ) : null}
        </>
      )}
    </section>
  );
}

export default PlatformBillingTab;
